import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useSetup2FAMutation, useEnable2FAMutation } from "../../api/apiSlice";

interface TwoFactorSetupProps {
  open: boolean;
  onClose: () => void;
  onSetupComplete: () => void;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ open, onClose, onSetupComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [setupData, setSetupData] = useState<any>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const [setup2FA] = useSetup2FAMutation();
  const [enable2FA, { isLoading: isEnabling }] = useEnable2FAMutation();

  const steps = ["Setup Authenticator", "Verify Code", "Save Backup Codes"];

  const handleStartSetup = useCallback(async () => {
    try {
      const response = await setup2FA().unwrap();
      const result = (response as any)?.data || response;
      setSetupData(result);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.data?.error?.message || err.data?.message || "Failed to start 2FA setup");
    }
  }, [setup2FA]);

  useEffect(() => {
    if (open && activeStep === 0 && !setupData) {
      handleStartSetup();
    }
  }, [open, activeStep, setupData, handleStartSetup]);

  const handleVerifyAndEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    try {
      const response = await enable2FA({
        token: verificationCode,
        setupToken: setupData.setupToken,
      }).unwrap();

      const result = (response as any)?.data || response;
      setBackupCodes(result.backupCodes);
      setActiveStep(2);
      setError("");
    } catch (err: any) {
      setError(err.data?.error?.message || err.data?.message || "Invalid verification code");
    }
  };

  const handleComplete = () => {
    setActiveStep(0);
    setSetupData(null);
    setVerificationCode("");
    setBackupCodes([]);
    setError("");
    onSetupComplete();
    onClose();
  };

  const handleClose = () => {
    if (activeStep === 2) {
      handleComplete();
    } else {
      setActiveStep(0);
      setSetupData(null);
      setVerificationCode("");
      setBackupCodes([]);
      setError("");
      onClose();
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setVerificationCode(value);
      setError("");
    }
  };

  const handleCopyBackupCodes = async () => {
    try {
      // Format with both newlines and spaces for better readability
      const codesText = backupCodes.map((code, index) => `${index + 1}. ${code}`).join("\n\n");
      await navigator.clipboard.writeText(codesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error("Failed to copy backup codes:", err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { minHeight: 500 } }}>
      <DialogTitle>Setup Two-Factor Authentication</DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Loading state for initial setup */}
        {activeStep === 0 && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Setting up 2FA...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            {/* Show retry button if setup failed */}
            {!setupData && activeStep === 0 && (
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" size="small" onClick={handleStartSetup}>
                  Try Again
                </Button>
              </Box>
            )}
          </Alert>
        )}

        {/* Step 1: Show QR Code and Secret */}
        {activeStep === 1 && setupData && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              1. Scan QR Code
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <img src={setupData.qrCode} alt="2FA QR Code" style={{ maxWidth: "200px", maxHeight: "200px" }} />
            </Box>

            <Typography variant="body2" sx={{ mb: 2 }}>
              Or manually enter this secret:
            </Typography>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: "#f5f5f5", border: "2px solid #ddd" }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "#000",
                  fontSize: "1.2rem",
                  letterSpacing: "0.1rem",
                }}
              >
                {setupData.secret}
              </Typography>
            </Paper>

            <Typography variant="h6" sx={{ mb: 2 }}>
              2. Enter Verification Code
            </Typography>
            <form onSubmit={handleVerifyAndEnable}>
              <TextField
                label="6-digit code"
                variant="outlined"
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="123456"
                error={!!error}
                helperText={error}
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: "center", fontSize: "1.2rem", letterSpacing: "0.3rem" },
                }}
                fullWidth
                margin="normal"
                autoFocus
              />
            </form>
          </Box>
        )}

        {/* Step 2: Show Backup Codes */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "success.main" }}>
              ✅ 2FA Enabled Successfully!
            </Typography>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Important: Save these backup codes in a safe place!
              </Typography>
              <Typography variant="body2">
                You can use these codes to access your account if you lose your authenticator device. Each code can only be used once.
              </Typography>
            </Alert>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Backup Codes:
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy all codes"}>
                <IconButton onClick={handleCopyBackupCodes} color={copied ? "success" : "primary"} size="small">
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>

            <Paper sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
              <List dense>
                {backupCodes.map((code, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "1rem" }}>
                          {code}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {activeStep === 1 && (
          <>
            <Button onClick={handleClose} disabled={isEnabling}>
              Cancel
            </Button>
            <Button onClick={handleVerifyAndEnable} variant="contained" disabled={isEnabling || verificationCode.length !== 6}>
              {isEnabling ? <CircularProgress size={24} /> : "Enable 2FA"}
            </Button>
          </>
        )}

        {activeStep === 2 && (
          <Button onClick={handleComplete} variant="contained" color="success">
            I've Saved These Codes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorSetup;
